import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { RevealLines, SectionLabel, EASE } from "./motion";
import { Symbol } from "./Logo";

const Badge = () => (
  <motion.div
    data-testid="page-hero-badge"
    initial={{ opacity: 0, scale: 0.7, rotate: -40 }}
    animate={{ opacity: 1, scale: 1, rotate: 0 }}
    transition={{ delay: 1.2, duration: 1.2, ease: EASE }}
    className="absolute -top-10 right-6 h-24 w-24 md:right-10 md:top-10 md:h-40 md:w-40"
  >
    <svg viewBox="0 0 200 200" className="orbit h-full w-full !border-0 text-cream/70">
      <defs><path id="badgeCircle" d="M100,100 m-78,0 a78,78 0 1,1 156,0 a78,78 0 1,1 -156,0" /></defs>
      <text className="fill-current font-mono text-[12px] uppercase tracking-[0.24em]"><textPath href="#badgeCircle">Skaris Technology LLP · IT · Digital · AI · </textPath></text>
    </svg>
    <span className="absolute inset-[30%] flex items-center justify-center rounded-full bg-skaris-navy shadow-[0_0_50px_rgba(61,127,217,0.5)]">
      <Symbol className="h-1/2 w-auto text-cream" />
    </span>
  </motion.div>
);

export const PageHero = ({ label, lines, intro, img, imgLabel, testid }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  return (
    <section ref={ref} data-testid={testid} className="relative overflow-hidden pt-36 md:pt-44">
      <div className="hex-pattern pointer-events-none absolute inset-0 opacity-40" />
      <div className="relative mx-auto max-w-[1440px] px-6 md:px-10">
        <Badge />
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9, duration: 0.8 }}>
          <SectionLabel>{label}</SectionLabel>
        </motion.div>
        <RevealLines
          lines={lines}
          delay={0.8}
          className="mt-8 font-display text-5xl font-semibold uppercase leading-[0.92] tracking-[-0.03em] text-cream sm:text-6xl lg:text-[7.5rem]"
          lineClass={(i) => (i === lines.length - 1 ? "text-gradient" : "")}
        />
        <div className="mt-12 grid gap-8 md:grid-cols-12">
          <motion.p
            className="font-body text-base leading-relaxed text-cream/75 md:col-span-5 md:col-start-8 md:text-lg"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 0.9, ease: EASE }}
          >
            {intro}
          </motion.p>
        </div>
      </div>
      {img && (
        <motion.div
          className="relative mx-auto mt-16 max-w-[1440px] px-6 md:px-10"
          initial={{ clipPath: "inset(18% 8% 18% 8% round 28px)" }}
          animate={{ clipPath: "inset(0% 0% 0% 0% round 28px)" }}
          transition={{ delay: 1.1, duration: 1.6, ease: EASE }}
        >
          <div className="relative h-[46vh] overflow-hidden rounded-[28px] md:h-[68vh]">
            <motion.img src={img} alt={imgLabel || "Skaris Technology"} style={{ y, scale }} className="duotone h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-skaris-ink/80 via-transparent to-transparent" />
            {imgLabel ? <span className="absolute bottom-5 left-6 font-mono text-[11px] uppercase tracking-[0.25em] text-cream/80">{imgLabel}</span> : null}
          </div>
        </motion.div>
      )}
    </section>
  );
};
